import { useCallback, useContext, useMemo } from 'react';

import dayjs from 'dayjs';

import { useAbsences, useAbsToolResources } from '@src/query';

import { ABS_DAY_VIEW, ABS_HALF_MONTH_VIEW, ABS_MONTH_VIEW } from './constants';
import { AbsenceTimelineContext } from './context';
import { prepareAbsencePayload } from './utils';
import { getAbsenceDataById } from './utils/absence';
import { getUserWorktimeForDate } from './utils/resources';

export default function useAbsencesTimeline() {
  const { data: absences, bookAbsence, deleteAbsences } = useAbsences();
  const { data: users } = useAbsToolResources();
  const { view, selectedSlot, setSelectedSlot, calendarRef, ...restOfContext } =
    useContext(AbsenceTimelineContext);

  const attachCalendar = useCallback(
    (instance) => {
      calendarRef.current = instance;
    },
    [calendarRef],
  );

  const getApi = useCallback(
    () => calendarRef.current?.getApi() ?? null,
    [calendarRef],
  );

  const calendar = useMemo(
    () => ({
      next: () => getApi()?.next(),
      prev: () => getApi()?.prev(),
      today: () => getApi()?.today(),
      getDate: () => getApi()?.getDate(),
      changeView: (v, date) => getApi()?.changeView(v, date),
      gotoDate: (d) => getApi()?.gotoDate(d),
      setMonthView: (year, month) => {
        getApi()?.changeView(ABS_MONTH_VIEW, new Date(year, month, 1));
      },
      setHalfMonthView: (year, monthIndex) => {
        const start = new Date(year, monthIndex, 15);
        const end = dayjs(start).add(1, 'month').toDate();
        getApi()?.changeView(ABS_HALF_MONTH_VIEW, start);
        getApi()?.setOption('visibleRange', { start, end });
      },
      setDayView: (date) => getApi()?.changeView(ABS_DAY_VIEW, date),
      setOption: (name, value) => getApi()?.setOption(name, value),
    }),
    [getApi],
  );

  const isMonthView = useMemo(() => view?.type === ABS_MONTH_VIEW, [view]);
  const isMonthOrHalfMonthView = useMemo(
    () => [ABS_HALF_MONTH_VIEW, ABS_MONTH_VIEW].includes(view?.type),
    [view],
  );

  const book = (values) => {
    return bookAbsence(prepareAbsencePayload(values), {
      onSuccess: () => setSelectedSlot(null),
    });
  };

  const remove = (ids) => {
    return deleteAbsences({ ids }, { onSuccess: () => setSelectedSlot(null) });
  };

  const selectedAbsencesById = useMemo(
    () => new Map(absences?.map((a) => [a.id, a])),
    [absences],
  );

  const isDifferentAbsencesSelected = useCallback(
    (ids) => {
      if (!ids || ids.length <= 1) {
        return false;
      }

      let firstType;
      for (const id of ids) {
        const obj = selectedAbsencesById.get(id);
        if (firstType === undefined) {
          firstType = obj.acti_type;
        } else if (obj.acti_type !== firstType) {
          return true;
        }
      }
      return false;
    },
    [selectedAbsencesById],
  );

  const getAbsenceTimeRange = useCallback(
    (slot) => {
      let startTime = slot.startTime;
      let endTime = slot.endTime;
      const userWorktime = getUserWorktimeForDate(
        slot.user_id,
        users,
        slot.date_from,
      );
      if (userWorktime && !slot.startTime && !slot.endTime) {
        startTime = userWorktime.start;
        endTime = userWorktime.end;
      }
      return {
        acti_start_time: startTime,
        acti_end_time: endTime,
      };
    },
    [users],
  );

  const selectedData = useMemo(() => {
    if (!selectedSlot) {
      return null;
    }

    const {
      absences_ids: selectedAbsencesIds,
      date_from,
      date_to,
    } = selectedSlot;
    const start = dayjs(date_from);
    const startDayNumber = start.day();
    const selectedData = {
      date_from: dayjs(date_from),
      date_to: dayjs(date_to),
      acti_type: null,
      ...(!isMonthOrHalfMonthView &&
        getAbsenceTimeRange(selectedSlot, startDayNumber)),
    };

    if (
      isDifferentAbsencesSelected(selectedAbsencesIds) ||
      !selectedAbsencesIds?.length
    ) {
      // Just showing selected dates for different selected absences
      // or selected range
      return selectedData;
    }

    const absenceId = selectedAbsencesIds[0];
    const { acti_type, acti_tentative, acab_notes } = getAbsenceDataById(
      absenceId,
      absences,
    );
    return { ...selectedData, acti_type, acti_tentative, acab_notes };
  }, [
    isMonthOrHalfMonthView,
    selectedSlot,
    isDifferentAbsencesSelected,
    absences,
    getAbsenceTimeRange,
  ]);

  const timeline = {
    calendar,
    view,
    selectedSlot,
    setSelectedSlot,
    attachCalendar,
    ...restOfContext,
    absences,
    users,
    book,
    selectedData,
    remove,
    isMonthView,
    isMonthOrHalfMonthView,
  };
  return timeline;
}
